import React, { useEffect } from 'react';
import { useApp } from '../../AppContext';

interface Props {
  title: string;
  subtitle?: string;
  lastUpdate?: string;
  children: React.ReactNode;
}

export default function LegalPage({ title, subtitle, lastUpdate, children }: Props) {
  const { goTo } = useApp();

  useEffect(() => {
    document.title = `${title} – Dr. Michele Balducci`;
    return () => { document.title = 'Dr. Michele Balducci – Psicologo & Psicoterapeuta'; };
  }, [title]);

  return (
    <div className="min-h-screen bg-beigeBG text-charcoal">
      <div className="fixed inset-0 pointer-events-none grain-overlay z-10" />

      <header className="sticky top-0 z-30 bg-beigeBG/95 backdrop-blur-md border-b border-forest/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => goTo('home')} className="flex flex-col group text-left">
            <span className="font-serif text-lg font-semibold tracking-tight text-forestDark">Dr. Michele Balducci</span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-sage font-medium -mt-1">Psicologo &amp; Psicoterapeuta</span>
          </button>
          <button
            onClick={() => goTo('home')}
            className="text-xs uppercase tracking-widest font-bold text-sage hover:text-forest smooth-transition border border-sage/30 hover:border-forest/50 px-4 py-2 rounded-full"
          >
            ← Torna al sito
          </button>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 py-12 md:py-16 relative z-20">
        <div className="space-y-3 mb-10 pb-8 border-b border-forest/10">
          <span className="text-xs uppercase tracking-[0.25em] text-sage font-semibold">Informativa</span>
          <h1 className="font-serif text-4xl md:text-5xl text-forestDark leading-tight">{title}</h1>
          {subtitle && <p className="text-sm text-charcoal/70 max-w-2xl">{subtitle}</p>}
          {lastUpdate && <p className="text-xs text-charcoal/50">Ultimo aggiornamento: {lastUpdate}</p>}
        </div>

        <div className="legal-content space-y-6 text-sm text-charcoal/85 leading-relaxed">
          {children}
        </div>

        <div className="mt-16 pt-8 border-t border-forest/10 text-xs text-charcoal/50 space-y-2">
          <p>Dr. Michele Balducci — Psicologo &amp; Psicoterapeuta</p>
          <p>Ordine degli Psicologi del Lazio, n. 21747 — P.IVA 13906801009</p>
          <p>Consulenza psicologica online — m.balducci1987@gmail.com — +39 347 2208426</p>
        </div>
      </article>
    </div>
  );
}

// Helper presentational
export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-serif text-2xl text-forestDark">{title}</h2>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

export function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 pt-2">
      <h3 className="font-semibold text-forestDark text-base">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
